/**
 * Where a dragged row landed, relative to a row that stayed put.
 *
 * `only` means the container held no other keyed row to anchor against.
 */
export type SortableMove =
  | { movedKey: string; position: 'before' | 'after'; anchorKey: string }
  | { movedKey: string; position: 'only' };

/**
 * Apply `move` to `items`, returning the same items in their new order.
 *
 * The result is always a permutation. Anchors resolve against the whole list, not the
 * container the drag happened in: a group's displayed order is a subsequence of the
 * list order, so anchoring globally also lands the item correctly within its group.
 *
 * An unresolvable key leaves the list untouched, so a stale read cannot reshuffle it.
 */
export function moveRelativeTo<T>(
  items: readonly T[],
  move: SortableMove,
  keyOf: (item: T) => string
): T[] {
  if (move.position === 'only') {
    return [...items];
  }

  const movedIndex = items.findIndex((item) => keyOf(item) === move.movedKey);
  if (movedIndex === -1) {
    return [...items];
  }

  const reordered = [...items];
  const [moved] = reordered.splice(movedIndex, 1);

  const anchorIndex = reordered.findIndex((item) => keyOf(item) === move.anchorKey);
  if (anchorIndex === -1) {
    return [...items];
  }

  reordered.splice(move.position === 'before' ? anchorIndex : anchorIndex + 1, 0, moved);
  return reordered;
}
